import { addYears, parse } from 'date-fns';
import * as React from 'react';
import cn from 'classnames';

import { useTheme } from '../../theme';
import { HighlightedDates } from '../types';
import { parseHighlightedDates, parseSelectedDays } from '../utils';
import { MonthsWrapper } from './MonthsWrapper/MonthsWrapper';
import { HoverDates } from '../../SearchForm/components/SearchForm/Segments/Segment/Datepicker/Datepicker';
import { MonthBaseDateVariant } from './MonthsWrapper/types';

export interface MonthRendererElements {
	monthHeader: React.ReactNode;
	weeklyHeader: React.ReactNode;
	daysByWeeks: React.ReactNodeArray[];
	weekClassName?: string;
	monthClassName?: string;
}

export interface RendererFunctionInterface {
	dayRenderer: (date: Date, className?: string) => React.ReactNode;
	monthRenderer: (params: MonthRendererElements) => React.ReactNode;
	monthHeaderAsPlainText: boolean;
}

export const minDefaultDate = '1910-01-01';

const RendererFunctions: RendererFunctionInterface = {
	dayRenderer(date: Date, className?: string): React.ReactNode {
		return <div className={className}>{date.getDate()}</div>;
	},
	monthRenderer(params: MonthRendererElements): React.ReactNode {
		return (
			<>
				{params.monthHeader}
				{params.weeklyHeader}
				<div className={params.monthClassName}>
					{params.daysByWeeks.map((day, index) => (
						<div key={index} className={params.weekClassName}>
							{day}
						</div>
					))}
				</div>
			</>
		);
	},
	monthHeaderAsPlainText: true
};

export interface DatepickerProps {
	/**
	 * Datepicker language
	 *
	 * @default 'en'
	 */
	locale?: string;

	/**
	 * What date will be focused
	 *
	 * @default new Date()
	 */
	openDate?: Date;

	/**
	 * Array of selected dates
	 *
	 * @default []
	 */
	selectedDates?: Date[];

	/**
	 * Map of highlighted dates
	 *
	 * @default {}
	 */
	highlightedDates?: HighlightedDates;

	/**
	 * Count of the months in datepicker block
	 *
	 * @default 1
	 */
	monthCount?: number;

	monthsToScroll?: number;

	monthBaseDateVariant?: MonthBaseDateVariant;

	children?: React.ReactNode;

	/**
	 * Select date handler
	 */
	onSelect: (date: Date) => void;

	/**
	 * Lowest available date
	 *
	 * @default new Date('1910-01-01')
	 */
	minDate?: Date;

	/**
	 * Highest available date
	 */
	maxDate?: Date;

	/**
	 * Month & year selector
	 *
	 * @default false
	 */
	monthHeaderAsSelector?: boolean;

	/**
	 * Get datepicker wrapper reference
	 */
	getRef?: (el: HTMLDivElement) => void;

	/**
	 * Renderer function for single day element
	 */
	dayRenderer?: (date: Date) => React.ReactNode;

	/**
	 * Render function for month
	 */
	monthRenderer?: (params: MonthRendererElements) => React.ReactNode;

	getParentRef?: () => React.RefObject<HTMLDivElement>;

	onHover?: (date: Date) => void;

	hoverDates?: HoverDates;

	frameless?: boolean;

	monthsHeader?: React.ReactNode;

	className?: string;
}

export const RendererFunctionsContext = React.createContext(RendererFunctions);

export const Datepicker: React.FunctionComponent<DatepickerProps> = props => {
	const monthCount = props.monthCount ? props.monthCount : 1;

	const initialDate = props.openDate ? props.openDate : new Date();
	const css = useTheme('Datepicker').PopupDatepickerStyles;

	const customFunctionsRenderer = { ...RendererFunctions };

	if (props.dayRenderer) {
		customFunctionsRenderer.dayRenderer = props.dayRenderer;
	}

	if (props.monthRenderer) {
		customFunctionsRenderer.monthRenderer = props.monthRenderer;
	}

	customFunctionsRenderer.monthHeaderAsPlainText = !props.monthHeaderAsSelector;

	return (
		<div className={cn(css.block, props.className)} ref={props.getRef ? props.getRef : null}>
			{props.children && <div>{props.children}</div>}

			<RendererFunctionsContext.Provider value={customFunctionsRenderer}>
				<MonthsWrapper
					getParentRef={props.getParentRef}
					initialDate={initialDate}
					monthCount={monthCount}
					monthsToScroll={props.monthsToScroll}
					monthBaseDateVariant={props.monthBaseDateVariant}
					onSelect={props.onSelect}
					onHover={props.onHover}
					hoverDates={props.hoverDates}
					minDate={props.minDate ? props.minDate : parse(minDefaultDate, 'yyyy-MM-dd', new Date())}
					maxDate={props.maxDate ? props.maxDate : addYears(initialDate, 1)}
					selectedDates={parseSelectedDays(props.selectedDates ? props.selectedDates : [])}
					highlightedDates={parseHighlightedDates(props.highlightedDates)}
					frameless={props.frameless}
					monthsHeader={props.monthsHeader}
				/>
			</RendererFunctionsContext.Provider>
		</div>
	);
};

export default Datepicker;
